import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLocalParticipant } from "@livekit/components-react";
import { Mic, MicOff } from "lucide-react";

/**
 * HostTopBarMic — Slice A of the Live UI Control-Clutter Cleanup.
 *
 * Renders the host's microphone toggle INSIDE the LiveRoomTopBar's right-side
 * cluster (via React Portal) so the host's top bar reads:
 *   [avatar + @handle chip] ................ [Mic] [X]
 *
 * Why a portal?
 *   `useLocalParticipant()` only has a valid RoomContext when the component
 *   is rendered inside <LiveKitRoom>. LiveRoomTopBar is deliberately mounted
 *   OUTSIDE the room provider (so it can survive token refresh unmounts).
 *   The top bar exposes a slot node with id `live-host-mic-slot`; this
 *   component targets that node and portals its button into it. The result
 *   is the mic sits inline with the X visually while the mic logic keeps
 *   its access to the LiveKit room.
 *
 * Mounted only when `canPublish && isHost`. The mic control for co-hosts
 * still ships in the LiveRoomAvControls dock (unchanged in Slice A) so
 * this component is intentionally host-scoped.
 */
export default function HostTopBarMic({ canPublish, isHost }) {
  const { localParticipant, isMicrophoneEnabled } = useLocalParticipant();
  const [slot, setSlot] = useState(null);

  // The slot node lives in LiveRoomTopBar which mounts as a sibling to
  // LiveRoomStage. It should be in the DOM by the time this component
  // paints, but we poll a couple of animation frames in case of a race.
  useEffect(() => {
    if (!canPublish || !isHost) return undefined;
    let cancelled = false;
    let raf = 0;
    const find = () => {
      if (cancelled) return;
      const node = document.getElementById("live-host-mic-slot");
      if (node) {
        setSlot(node);
      } else {
        raf = requestAnimationFrame(find);
      }
    };
    find();
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      setSlot(null);
    };
  }, [canPublish, isHost]);

  const toggleMic = useCallback(async () => {
    if (!localParticipant) return;
    try {
      await localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled);
    } catch {
      /* LiveKit surfaces the error via its own logger */
    }
  }, [localParticipant, isMicrophoneEnabled]);

  if (!canPublish || !isHost || !localParticipant || !slot) return null;

  return createPortal(
    <button
      type="button"
      data-testid="live-host-mic-toggle"
      onClick={toggleMic}
      title={isMicrophoneEnabled ? "Mute microphone" : "Unmute microphone"}
      aria-pressed={!isMicrophoneEnabled}
      className={`w-9 h-9 rounded-full backdrop-blur-md border flex items-center justify-center transition-colors ${
        isMicrophoneEnabled
          ? "bg-black/45 border-white/10 text-white hover:border-white/30"
          : "bg-rose-600 border-rose-500 text-white shadow-lg shadow-rose-500/30"
      }`}
    >
      {isMicrophoneEnabled ? <Mic size={16} /> : <MicOff size={16} />}
    </button>,
    slot,
  );
}
